/**
 * PCI Proxy Secure Fields wrapper for card number and CVC.
 * Renders placeholder divs; initializes with merchantId (Mews PublicKey).
 * Submit returns transactionId for CreditCardData.PaymentGatewayData.
 *
 * @see STEP_4_4_OPTION_B_TASKMASTER.md - Task 5
 * @see supporting-payment-cards.md - PCI Proxy Secure Fields, use PublicKey as merchantId
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { loadPciProxySecureFieldsScript } from "@/services/mews-api";
import type { SecureFieldsChangeData, SecureFieldsValidateData } from "@/types/secure-fields";

const CARD_NUMBER_PLACEHOLDER_ID = "secure-fields-card-number";
const CVV_PLACEHOLDER_ID = "secure-fields-cvv";

/** Base path for card/CVC thumbnails (card type and CVV state icons). */
const SECURE_FIELDS_ICONS_BASE = "/assets/secure-fields";

/** Map API paymentMethod values to our brand asset filenames (uppercase 3-letter codes). */
const PAYMENT_METHOD_TO_BRAND: Record<string, string> = {
  HYPERCARD: "HPC",
};

function getSecureFieldIconSrc(iconType: string): string {
  return `${SECURE_FIELDS_ICONS_BASE}/${iconType}.svg`;
}

function paymentMethodToBrandKey(paymentMethod: string): string {
  const upper = String(paymentMethod).toUpperCase();
  const brand = PAYMENT_METHOD_TO_BRAND[upper] ?? upper;
  return `brands/${brand}`;
}

export interface SecureFieldsCardInputRef {
  /** Submit card data; returns transactionId for PaymentGatewayData. Expiration in YYYY-MM. */
  submit(expirationYYYYMM: string): Promise<string>;
  /** Whether Secure Fields are ready to submit. */
  isReady: boolean;
}

export interface SecureFieldsCardInputProps {
  /** Mews PaymentGateway.PublicKey (merchantId for PCI Proxy). */
  publicKey: string;
  /** Ref to call submit() and check isReady from parent. */
  ref?: React.RefObject<SecureFieldsCardInputRef | null>;
  /** Called when init fails (e.g. script error). */
  onInitError?: (error: Error) => void;
  /** Called when Secure Fields iframes are ready to accept input. */
  onReady?: () => void;
  /**
   * Fired when Secure Fields reports `valid` for card number and/or CVV (change + validate events).
   * Both must be true before treating the hosted fields as complete.
   */
  onFieldsValidityChange?: (state: { cardNumberValid: boolean; cvvValid: boolean }) => void;
  /** Optional class name for the container. */
  className?: string;
  /** Rendered between card number and CVC (e.g. Expiration field). Shown on same row as CVC. */
  children?: React.ReactNode;
}

/**
 * Parses YYYY-MM to expm (1-12) and expy (2-digit year) for DataTrans submit.
 */
function parseExpiration(yyyyMm: string): { expm: number; expy: number } | null {
  const match = /^(\d{4})-(\d{2})$/.exec(yyyyMm.trim());
  if (!match) return null;
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  if (month < 1 || month > 12) return null;
  return { expm: month, expy: year % 100 };
}

/**
 * Secure Fields card number + CVC iframes. Card data never touches React state or app DOM.
 */
function patchValidityFromPayload(
  fields: SecureFieldsChangeData["fields"] | undefined,
  store: { cardNumberValid: boolean; cvvValid: boolean },
): boolean {
  if (!fields) return false;
  let changed = false;
  const cn = fields.cardNumber;
  if (cn && typeof cn.valid === "boolean" && store.cardNumberValid !== cn.valid) {
    store.cardNumberValid = cn.valid;
    changed = true;
  }
  const cv = fields.cvv;
  if (cv && typeof cv.valid === "boolean" && store.cvvValid !== cv.valid) {
    store.cvvValid = cv.valid;
    changed = true;
  }
  return changed;
}

function SecureFieldsCardInputInner(
  {
    publicKey,
    onInitError,
    onReady,
    onFieldsValidityChange,
    className,
    children,
  }: Omit<SecureFieldsCardInputProps, "ref">,
  ref: React.Ref<SecureFieldsCardInputRef | null>
) {
  const [isReady, setIsReady] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  /** Icon type for card number: card-empty | card-error | card-recognized | brands/VIS etc. */
  const [cardIcon, setCardIcon] = useState<string>("card-empty");
  /** Icon type for CVC: cvc-empty | cvc-error */
  const [cvvIcon, setCvvIcon] = useState<string>("cvc-empty");
  const instanceRef = useRef<ReturnType<typeof window.SecureFields> | null>(null);
  const resolveSubmitRef = useRef<(value: string) => void | null>(null);
  const rejectSubmitRef = useRef<(reason: Error) => void | null>(null);
  const onReadyRef = useRef(onReady);
  const onInitErrorRef = useRef(onInitError);
  const onFieldsValidityChangeRef = useRef(onFieldsValidityChange);
  const fieldsValidityStoreRef = useRef({ cardNumberValid: false, cvvValid: false });
  onReadyRef.current = onReady;
  onInitErrorRef.current = onInitError;
  onFieldsValidityChangeRef.current = onFieldsValidityChange;

  const notifyFieldsValidity = useCallback(() => {
    const notify = onFieldsValidityChangeRef.current;
    if (!notify) return;
    notify({ ...fieldsValidityStoreRef.current });
  }, []);

  useEffect(() => {
    if (!publicKey?.trim()) return;

    let cancelled = false;
    console.log("[SecureFields] init: starting", { publicKey: publicKey?.slice(0, 8) + "…" });

    loadPciProxySecureFieldsScript()
      .then(() => {
        if (cancelled || typeof window.SecureFields !== "function") {
          console.log("[SecureFields] init: cancelled or no SecureFields");
          return;
        }
        const SecureFieldsCtor = window.SecureFields!;
        const sf = new SecureFieldsCtor() as import("@/types/secure-fields").SecureFieldsInstance;
        instanceRef.current = sf;

        sf.on("ready", () => {
          if (!cancelled) {
            console.log("[SecureFields] init: ready");
            setIsReady(true);
            // Defer so parent setState runs in next tick (avoids React batching dropping update when callback is from third-party script)
            const notify = onReadyRef.current;
            if (notify) setTimeout(() => notify(), 0);
          }
        });

        // PCI Proxy/Datatrans passes the payload directly: { transactionId, result?, cardInfo? } (not ev.data)
        sf.on("success", (ev: { transactionId?: string; result?: string; cardInfo?: unknown }) => {
          const transactionId = ev?.transactionId;
          console.log("[SecureFields] submit: success", { transactionId: transactionId ? transactionId.slice(0, 12) + "…" : null });
          const resolve = resolveSubmitRef.current;
          const reject = rejectSubmitRef.current;
          resolveSubmitRef.current = null;
          rejectSubmitRef.current = null;
          if (transactionId && resolve) resolve(transactionId);
          else if (reject) reject(new Error("Card verification did not return a transaction. Please try again."));
        });

        sf.on("error", () => {
          console.log("[SecureFields] submit: error event from Datatrans");
          const reject = rejectSubmitRef.current;
          resolveSubmitRef.current = null;
          rejectSubmitRef.current = null;
          if (reject) reject(new Error("Secure Fields submission failed."));
        });

        sf.on("change", (data: SecureFieldsChangeData) => {
          if (cancelled) return;
          if (patchValidityFromPayload(data?.fields, fieldsValidityStoreRef.current)) {
            notifyFieldsValidity();
          }

          const cn = data?.fields?.cardNumber;
          if (cn) {
            if (cn.valid === false) setCardIcon("card-error");
            else if (cn.paymentMethod) setCardIcon(paymentMethodToBrandKey(cn.paymentMethod));
            else setCardIcon("card-empty");
          }

          const cv = data?.fields?.cvv;
          if (cv) {
            if (cv.valid === false) setCvvIcon("cvc-error");
            else setCvvIcon("cvc-empty");
          }
        });

        sf.on("validate", (data: SecureFieldsValidateData) => {
          if (cancelled) return;
          if (patchValidityFromPayload(data?.fields, fieldsValidityStoreRef.current)) {
            notifyFieldsValidity();
          }
          if (data?.fields?.cardNumber?.valid === false) setCardIcon("card-error");
          if (data?.fields?.cvv?.valid === false) setCvvIcon("cvc-error");
        });

        sf.initTokenize(
          publicKey.trim(),
          {
            cardNumber: CARD_NUMBER_PLACEHOLDER_ID,
            cvv: CVV_PLACEHOLDER_ID,
          }
          // No focus option — avoids stealing focus from cardholder name when script loads late
        );
      })
      .catch((err) => {
        if (cancelled) return;
        console.log("[SecureFields] init: script load failed", err);
        const message = err instanceof Error ? err.message : "Failed to load Secure Fields.";
        setInitError(message);
        fieldsValidityStoreRef.current = { cardNumberValid: false, cvvValid: false };
        notifyFieldsValidity();
        const notify = onInitErrorRef.current;
        if (notify) setTimeout(() => notify(err instanceof Error ? err : new Error(message)), 0);
      });

    return () => {
      console.log("[SecureFields] cleanup: effect re-run or unmount", { hasPendingSubmit: !!rejectSubmitRef.current });
      cancelled = true;
      instanceRef.current = null;
      fieldsValidityStoreRef.current = { cardNumberValid: false, cvvValid: false };
      notifyFieldsValidity();
      const reject = rejectSubmitRef.current;
      if (reject) reject(new Error("Payment was interrupted. Please try again."));
      resolveSubmitRef.current = null;
      rejectSubmitRef.current = null;
    };
    // Only re-init when publicKey changes. Callbacks read via refs to avoid effect re-run on every parent render.
  }, [publicKey, notifyFieldsValidity]);

  const submit = useCallback(
    (expirationYYYYMM: string): Promise<string> =>
      new Promise((resolve, reject) => {
        console.log("[SecureFields] submit: called", { expirationYYYYMM });
        const parsed = parseExpiration(expirationYYYYMM);
        if (!parsed) {
          console.log("[SecureFields] submit: invalid expiration");
          reject(new Error("Invalid expiration format. Use YYYY-MM."));
          return;
        }
        const sf = instanceRef.current;
        if (!sf) {
          console.log("[SecureFields] submit: no instance");
          reject(new Error("Secure Fields not initialized."));
          return;
        }
        resolveSubmitRef.current = resolve;
        rejectSubmitRef.current = reject;
        // Defer submit to next tick so any focus/blur from the button click is processed first.
        const submitPayload = { expm: parsed.expm, expy: parsed.expy };
        setTimeout(() => {
          if (resolveSubmitRef.current !== resolve || !instanceRef.current) {
            console.log("[SecureFields] submit: skipped in setTimeout (unmounted or resolved)", {
              stillHasResolve: resolveSubmitRef.current === resolve,
              hasInstance: !!instanceRef.current,
            });
            return;
          }
          console.log("[SecureFields] submit: calling sf.submit()");
          instanceRef.current.submit(submitPayload);
        }, 0);
      }),
    []
  );

  React.useImperativeHandle(
    ref,
    () => ({
      submit,
      get isReady() {
        return isReady;
      },
    }),
    [submit, isReady]
  );

  return (
    <div className={className}>
      {/* Constrain DataTrans iframes to single-line height (they inject into these divs) */}
      <style>{`
        #${CARD_NUMBER_PLACEHOLDER_ID} iframe,
        #${CVV_PLACEHOLDER_ID} iframe {
          max-height: 42px !important;
          height: 42px !important;
        }
      `}</style>
      <div className="space-y-4 w-full">
        {/* Row 1: Card number with card-type thumbnail on the right */}
        <div>
          <label
            htmlFor={CARD_NUMBER_PLACEHOLDER_ID}
            className="block text-sm font-medium text-neutral-900 mb-1"
          >
            Card Number
          </label>
          <div className="relative flex h-[42px] w-full">
            <div
              id={CARD_NUMBER_PLACEHOLDER_ID}
              className="h-full flex-1 overflow-hidden rounded-lg border border-gray-300 bg-white pl-3 pr-12 py-2 text-neutral-900 box-border"
            />
            <div
              className="absolute inset-0 flex justify-end items-center pr-3 pointer-events-none"
              aria-hidden
            >
              <img
                src={getSecureFieldIconSrc(cardIcon)}
                alt=""
                className="h-6 w-auto max-h-full object-contain"
              />
            </div>
          </div>
        </div>
        {/* Row 2: Validity + CVC on same line (mobile: 2 cols; desktop: fixed widths) */}
        <div className="grid grid-cols-2 sm:grid-cols-[140px_100px] gap-4 items-end">
          <div className="min-w-0">{children}</div>
          <div className="min-w-0">
            <label htmlFor={CVV_PLACEHOLDER_ID} className="block text-sm font-medium text-neutral-900 mb-1">
              CVC
            </label>
            <div className="relative flex h-[42px] w-full">
              <div
                id={CVV_PLACEHOLDER_ID}
                className="h-full flex-1 overflow-hidden rounded-lg border border-gray-300 bg-white pl-3 pr-10 py-2 text-neutral-900 box-border min-w-0"
              />
              <div
                className="absolute inset-0 flex justify-end items-center pr-2.5 pointer-events-none"
                aria-hidden
              >
                <img
                  src={getSecureFieldIconSrc(cvvIcon)}
                  alt=""
                  className="h-5 w-auto max-h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {initError && (
        <p className="mt-2 text-sm text-amber-700" role="alert">
          {initError}
        </p>
      )}
    </div>
  );
}

export const SecureFieldsCardInput = React.forwardRef(SecureFieldsCardInputInner);
